import { X } from 'lucide-react';
import React, { useEffect, useRef } from 'react';
import { useDesignColors } from '../../hooks/useDesignColors';
import { useMiniAppModal } from '../../hooks/useMiniAppModal';
import { useMiniApps } from '../../hooks/useMiniApps';
import { useThemeSettings } from '../../hooks/useThemeSettings';
import type { MiniAppId } from '../../utils/miniAppsIcons';
import { CalendarApp } from './CalendarApp';
import { GalleryApp } from './GalleryApp';
import { ProductsApp } from './ProductsApp';
import { RealEstateApp } from './RealEstateApp';
import { ServicesApp } from './ServicesApp';
import { VideosApp } from './VideosApp';
import { SocialMediaApp } from './SocialMediaApp';

const MINI_APP_COMPONENTS: Record<MiniAppId, React.ComponentType> = {
  products: ProductsApp,
  calendar: CalendarApp,
  services: ServicesApp,
  videos: VideosApp,
  gallery: GalleryApp,
  realestate: RealEstateApp,
  social: SocialMediaApp
};

const DEFAULT_TITLES: Record<MiniAppId, string> = {
  products: 'Our products',
  calendar: 'Book an appointment',
  services: 'Our services',
  videos: 'Videos',
  gallery: 'Photo gallery',
  realestate: 'Real estate',
  social: 'Social Media'
};

export function MiniAppModal() {
  const { activeModal, closeModal, setActiveApp } = useMiniAppModal();
  const { theme } = useThemeSettings();
  const { colors } = useDesignColors();
  const { miniApps } = useMiniApps();
  const modalRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const isDark = theme.mode === 'dark';

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        closeModal();
      }
    };

    if (activeModal) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeModal, closeModal]);

  // Animation effect when modal opens
  useEffect(() => {
    if (activeModal && modalRef.current && contentRef.current) {
      // Apply entrance animation to the modal backdrop
      modalRef.current.animate(
        [
          { opacity: 0 },
          { opacity: 1 }
        ],
        { duration: 300, easing: 'ease-out', fill: 'forwards' }
      );

      // Apply entrance animation to the modal content
      contentRef.current.animate(
        [
          { opacity: 0, transform: 'scale(0.95) translateY(10px)' },
          { opacity: 1, transform: 'scale(1) translateY(0)' }
        ],
        { duration: 400, easing: 'cubic-bezier(0.16, 1, 0.3, 1)', fill: 'forwards' }
      );
    }
  }, [activeModal]);

  // Handle close with animation
  const handleClose = () => {
    if (modalRef.current && contentRef.current) {
      // Animate the modal content out
      const contentAnimation = contentRef.current.animate(
        [
          { opacity: 1, transform: 'scale(1) translateY(0)' },
          { opacity: 0, transform: 'scale(0.95) translateY(10px)' }
        ],
        { duration: 250, easing: 'cubic-bezier(0.7, 0, 0.84, 0)', fill: 'forwards' }
      );

      // Animate the backdrop out
      const backdropAnimation = modalRef.current.animate(
        [
          { opacity: 1 },
          { opacity: 0 }
        ],
        { duration: 200, easing: 'ease-in', fill: 'forwards' }
      );

      // Close the modal after animations complete
      backdropAnimation.onfinish = () => closeModal();
    } else {
      closeModal();
    }
  };

  // Gestionnaire pour l'événement 'open-products-miniapp'
  useEffect(() => {
    const handleOpenProductsMiniApp = (event: CustomEvent) => {
      const { productIds } = event.detail || {};
      console.log('MiniAppModal: Événement open-products-miniapp reçu avec IDs:', productIds);
      
      if (productIds && Array.isArray(productIds) && productIds.length > 0) {
        const filters = {
          products: {
            mentionedIds: productIds
          }
        };
        
        // Ouvrir la mini-app des produits avec les filtres
        if (setActiveApp) {
          setActiveApp('products', filters);
          console.log('MiniAppModal: Mini-app produits ouverte avec les filtres:', filters);
        }
      }
    };
    
    // Ajouter l'écouteur d'événements
    document.addEventListener('open-products-miniapp', handleOpenProductsMiniApp as EventListener);
    
    // Nettoyage
    return () => {
      document.removeEventListener('open-products-miniapp', handleOpenProductsMiniApp as EventListener);
    };
  }, [setActiveApp]);

  if (!activeModal) return null;

  const AppComponent = MINI_APP_COMPONENTS[activeModal];
  const activeApp = miniApps.find(app => app.id === activeModal);
  const title = activeApp?.label || DEFAULT_TITLES[activeModal];

  // Define the modal size based on the type of app
  const getModalSize = () => {
    switch (activeModal) {
      case 'realestate':
        return 'md:w-[90vw] md:max-w-[1600px] md:h-[80vh]'; // More compact for real estate
      case 'products':
        return 'md:w-[80vw] md:max-w-[1200px] md:h-[70vh]'; // More compact for products
      case 'gallery':
        return 'md:w-[80vw] md:max-w-[1200px] md:h-[70vh]'; // More compact for gallery
      case 'videos':
        return 'md:w-[80vw] md:max-w-[1200px] md:h-[70vh]'; // More compact for videos
      case 'services':
        return 'md:w-full md:max-w-4xl md:h-[700px]'; // Increased height for services
      case 'calendar':
        return 'md:w-full md:max-w-2xl md:h-[600px]'; // Fixed size for calendar
      case 'social':
        return 'md:w-full md:max-w-xl md:h-[400px]'; // Compact size for social media
      default:
        return 'md:w-full md:max-w-2xl md:h-[500px]'; // Default size
    }
  };

  return (
    <div 
      ref={modalRef}
      className="fixed inset-0 z-[1000000] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      style={{ opacity: 0 }} // Initial state for animation
    >
      <div 
        ref={contentRef}
        className={`${getModalSize()} rounded-xl shadow-xl w-full h-full ${
          isDark 
            ? 'bg-gray-900 border border-gray-700' 
            : 'bg-white'
        }`}
        style={{ 
          opacity: 0, 
          transform: 'scale(0.95) translateY(10px)' // Initial state for animation
        }}
      >
        <div className={`flex items-center justify-between p-4 border-b ${
          isDark ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <h2 
            className="text-xl font-semibold"
            style={{
              background: `linear-gradient(to right, ${colors.gradientStart}, ${colors.gradientEnd})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}
          >
            {title}
          </h2>
          <button
            onClick={handleClose}
            className={`p-2 rounded-lg transition-colors ${
              isDark 
                ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-800' 
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className={`${isDark ? 'text-gray-200' : 'text-gray-900'} h-[calc(100%-4rem)] overflow-hidden`}>
          <AppComponent />
        </div>
      </div>
    </div>
  );
}